import { useTranslation, t } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  DatePicker,
  Select,
  Space,
  message,
  Input,
  Button,
  Tooltip,
  Typography,
  Popover,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  BarChartOutlined,
  SearchOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import llmService from '../../services/llmService';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

// 配额转换为美元 (500000 单位 = $1)
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(2)}K`;
  }
  if (dollars >= 0.01) {
    return `$${dollars.toFixed(2)}`;
  }
  return `$${dollars.toFixed(4)}`;
};

// 格式化数字
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '0';
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toLocaleString();
};

// 渲染类型标签
const renderType = (type) => {
  switch (type) {
    case 1:
      return <Tag color="cyan">{t('Top up')}</Tag>;
    case 2:
      return <Tag color="lime">{t('Spending')}</Tag>;
    case 3:
      return <Tag color="orange">{t('Manage')}</Tag>;
    case 4:
      return <Tag color="purple">{t('System')}</Tag>;
    case 5:
      return <Tag color="red">{t('Error')}</Tag>;
    default:
      return <Tag color="default">{t('Unknown')}</Tag>;
  }
};

// 渲染用时标签
const renderUseTime = (time) => {
  const t = parseInt(time) || 0;
  if (t < 10) {
    return <Tag color="green">{t}s</Tag>;
  } else if (t < 30) {
    return <Tag color="orange">{t}s</Tag>;
  } else {
    return <Tag color="red">{t}s</Tag>;
  }
};

// 渲染首字时间
const renderFirstTime = (time) => {
  if (!time) return null;
  const t = (parseFloat(time) / 1000).toFixed(1);
  if (parseFloat(t) < 3) {
    return <Tag color="green">{t}s</Tag>;
  } else if (parseFloat(t) < 10) {
    return <Tag color="orange">{t}s</Tag>;
  } else {
    return <Tag color="red">{t}s</Tag>;
  }
};

// 渲染流/非流标签
const renderStreamTag = (isStream) => {
  if (isStream) {
    return <Tag color="blue">{t('Stream')}</Tag>;
  }
  return <Tag color="purple">{t('Non-streaming')}</Tag>;
};

const Logs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ total: 0, success: 0, error: 0, totalQuota: 0 });

  useEffect(() => {
    fetchLogs();
  }, [pagination.current, pagination.pageSize, filters, dateRange]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        p: pagination.current - 1,
        page_size: pagination.pageSize,
        ...filters,
      };

      if (dateRange && dateRange.length === 2) {
        params.start_timestamp = Math.floor(dateRange[0].startOf('day').valueOf() / 1000);
        params.end_timestamp = Math.floor(dateRange[1].endOf('day').valueOf() / 1000);
      }

      const response = await llmService.getLogs(params);
      if (response.success) {
        const items = response.data?.items || [];
        setLogs(items);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
        }));

        // 计算统计
        const successCount = items.filter(item => item.type === 2).length;
        const errorCount = items.filter(item => item.type === 5).length;
        const totalQuota = items.reduce((sum, item) => sum + (item.quota || 0), 0);
        setStats({
          total: response.data?.total || 0,
          success: successCount,
          error: errorCount,
          totalQuota,
        });
      } else {
        message.error(response.error || t('Failed to retrieve logs'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve logs'));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(t('Copied'));
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success(t('Copied'));
    } catch (err) {
      message.error(t('Failed to copy'));
    }
    document.body.removeChild(textarea);
  };

  // 解析 other 字段
  const parseOther = (other) => {
    if (!other) return null;
    try {
      return JSON.parse(other);
    } catch (e) {
      return null;
    }
  };

  const columns = [
    {
      title: t('Time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      fixed: 'left',
      render: (time) => time ? dayjs(time * 1000).format('MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('Token'),
      dataIndex: 'token_name',
      key: 'token_name',
      width: 120,
      render: (text) => text ? (
        <Tooltip title={text}>
          <Tag
            color="default"
            style={{ cursor: 'pointer' }}
            onClick={() => copyToClipboard(text)}
          >
            {text.length > 10 ? `${text.substring(0, 10)}...` : text}
          </Tag>
        </Tooltip>
      ) : '-',
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: renderType,
    },
    {
      title: t('Model'),
      dataIndex: 'model_name',
      key: 'model_name',
      width: 180,
      render: (model, record) => {
        const other = parseOther(record.other);
        const isMapped = other?.is_model_mapped && other?.upstream_model_name;

        if (!model) return '-';

        if (isMapped) {
          return (
            <Popover
              content={
                <div>
                  <div><Text strong>{t('Requested model:')}</Text>{model}</div>
                  <div><Text strong>{t('Actual model:')}</Text>{other.upstream_model_name}</div>
                </div>
              }
              title={t('Model mapping')}
            >
              <Tag
                color="blue"
                style={{ cursor: 'pointer' }}
                onClick={() => copyToClipboard(model)}
              >
                {model.length > 20 ? `${model.substring(0, 20)}...` : model}
                <SyncOutlined style={{ marginLeft: 4, fontSize: 10 }} />
              </Tag>
            </Popover>
          );
        }

        return (
          <Tooltip title={model}>
            <Tag
              color="blue"
              style={{ cursor: 'pointer' }}
              onClick={() => copyToClipboard(model)}
            >
              {model.length > 20 ? `${model.substring(0, 20)}...` : model}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('Duration/first token'),
      dataIndex: 'use_time',
      key: 'use_time',
      width: 140,
      render: (time, record) => {
        if (record.type !== 2 && record.type !== 5) return '-';
        const other = parseOther(record.other);
        return (
          <Space size={4}>
            {renderUseTime(time)}
            {record.is_stream && renderFirstTime(other?.frt)}
            {renderStreamTag(record.is_stream)}
          </Space>
        );
      },
    },
    {
      title: t('Input'),
      dataIndex: 'prompt_tokens',
      key: 'prompt_tokens',
      width: 80,
      align: 'right',
      render: (tokens, record) => (record.type === 2 || record.type === 5) ? formatNumber(tokens) : '-',
    },
    {
      title: t('Output'),
      dataIndex: 'completion_tokens',
      key: 'completion_tokens',
      width: 80,
      align: 'right',
      render: (tokens, record) => (record.type === 2 || record.type === 5) && tokens > 0 ? formatNumber(tokens) : '-',
    },
    {
      title: t('Cost'),
      dataIndex: 'quota',
      key: 'quota',
      width: 100,
      align: 'right',
      render: (quota, record) => (record.type === 2 || record.type === 5) ? (
        <Text type={quota > 0 ? 'warning' : 'secondary'}>{quotaToDollar(quota)}</Text>
      ) : '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (ip, record) => {
        if ((record.type === 2 || record.type === 5) && ip) {
          return (
            <Tooltip title={ip}>
              <Tag
                color="orange"
                style={{ cursor: 'pointer' }}
                onClick={() => copyToClipboard(ip)}
              >
                {ip.length > 15 ? `${ip.substring(0, 15)}...` : ip}
              </Tag>
            </Tooltip>
          );
        }
        return '-';
      },
    },
    {
      title: t('Details'),
      dataIndex: 'content',
      key: 'content',
      width: 200,
      fixed: 'right',
      render: (content, record) => {
        const other = parseOther(record.other);

        // 构建详情内容
        let detailContent = content || '';
        if (other && record.type === 2) {
          const details = [];
          if (other.model_ratio !== undefined) details.push(t('Model multiplier: {{model_ratio}}', { model_ratio: other.model_ratio }));
          if (other.group_ratio !== undefined) details.push(t('Group multiplier: {{group_ratio}}', { group_ratio: other.group_ratio }));
          if (other.cache_tokens > 0) details.push(t('Cached tokens: {{cache_tokens}}', { cache_tokens: other.cache_tokens }));
          detailContent = details.length > 0 ? details.join('\n') : content;
        }

        if (!detailContent) return '-';

        return (
          <Popover
            content={
              <div style={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>
                {detailContent}
              </div>
            }
            title={t('Details')}
            trigger="click"
          >
            <Paragraph
              ellipsis={{ rows: 1 }}
              style={{ marginBottom: 0, cursor: 'pointer', maxWidth: 180 }}
            >
              {detailContent.split('\n')[0]}
              <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
            </Paragraph>
          </Popover>
        );
      },
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Total records')}
              value={stats.total}
              prefix={<BarChartOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Successful requests')}
              value={stats.success}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Failed requests')}
              value={stats.error}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Page usage')}
              value={quotaToDollar(stats.totalQuota)}
              prefix={<ThunderboltOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>{t('Usage logs')}</span>
          </Space>
        }
        style={{ borderRadius: 12 }}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            {t('Refresh')}
          </Button>
        }
      >
        {/* 筛选区域 */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <Input
              placeholder={t('Search model name')}
              prefix={<SearchOutlined />}
              value={filters.model_name}
              onChange={(e) => handleFilterChange('model_name', e.target.value)}
              style={{ width: 180 }}
              allowClear
            />
            <Input
              placeholder={t('Search token name')}
              prefix={<SearchOutlined />}
              value={filters.token_name}
              onChange={(e) => handleFilterChange('token_name', e.target.value)}
              style={{ width: 150 }}
              allowClear
            />
            <Select
              placeholder={t('Type')}
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              style={{ width: 100 }}
              allowClear
              options={[
                { value: 1, label: t('Top up') },
                { value: 2, label: t('Spending') },
                { value: 3, label: t('Manage') },
                { value: 4, label: t('System') },
                { value: 5, label: t('Error') },
              ]}
            />
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              placeholder={[t('Start date'), t('End date')]}
              style={{ width: 240 }}
              allowClear
            />
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('{{total}} records total', { total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          size="small"
          rowClassName={(record) => {
            if (record.type === 5) return 'log-row-error';
            return '';
          }}
        />
      </Card>

      <style>{`
        .log-row-error {
          background-color: #fff2f0;
        }
        .log-row-error:hover > td {
          background-color: #ffebe6 !important;
        }
      `}</style>
    </div>
  );
};

export default Logs;
